import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { UseCases } from "@planzone/core";
import {
  useAuthentication,
  useExportData,
  useRetrieveProjectBudgetSynthesis,
  useRetrieveProjectExpenses,
} from "@planzone/view-models";
import { DateTimeInput } from "@common/dateTimeInput";
import { useModal } from "@root/client/modal";
import { Props } from "./types";
import { Section } from "./section";
import { PlannedBudget } from "./plannedBudget";
import { DateMonitoring } from "./dateMonitoring";
import { CostsBreakdownByResourceType } from "./costsBreakdownByResourceType";
import { ExpensesBreakdownByExpenses } from "./expensesBreakdownByExpenseType";
import { ExpensesTable } from "./expensesTable";
import { ExportButton } from "./exportButton";
import styles from "./styles.scss";
import { ExportsDataTypes, ExportsFormats } from "@planzone/dto";
import { useAllocationUnit } from "@common/allocationUnit";

function BudgetPage({ project }: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { exportData } = useExportData();
  const timeUnit = useAllocationUnit({});
  const [cursor, setCursor] = React.useState(new Date().toISOString());
  const { pushView } = useModal();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    synthesis,
    retrieveProjectBudgetSynthesis,
  } = useRetrieveProjectBudgetSynthesis(project, cursor);

  const { projectExpenses, retrieveProjectExpenses } =
    useRetrieveProjectExpenses();

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectBudgetSynthesis({
        project,
        date: cursor,
      });
  }, [
    cursor,
    project.uuid,
    synthesis.allocatedBudget,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
  ]);

  React.useEffect(() => {
    retrieveProjectExpenses(project);
  }, [project.uuid]);

  return (
    <div className={styles.budgetPage}>
      <Helmet>
        <title>Planzone • {t("projectBudgetSynthesis.title")}</title>
        <meta name="description" content={project.description} />
      </Helmet>
      <div className={styles.main}>
        <div className={styles.menu}>
          <DateTimeInput
            dateTime={cursor}
            projectTimezone={project.timeZone}
            placeholder=""
            input={setCursor}
          />
          <button
            className={styles.button}
            onClick={() =>
              pushView({
                key: UseCases.createProjectExpense,
                data: project,
              })
            }
          >
            <div className={styles.iconBox}>+</div>
            <p>{t("projectBudgetSynthesis.expenses.create")}</p>
          </button>
        </div>
        <div className={styles.slantedRow}>
          <Section title={t("projectBudgetSynthesis.plannedBudget.title")}>
            <PlannedBudget synthesis={synthesis} project={project} />
          </Section>
          <Section title={t("projectBudgetSynthesis.monitoring.title")}>
            <div className={styles.monitoring}>
              <DateMonitoring synthesis={synthesis} />
            </div>
          </Section>
        </div>
        <div className={styles.row}>
          <Section
            title={t(
              "projectBudgetSynthesis.expensesBreakdownByExpenseType.title",
            )}
          >
            <ExpensesBreakdownByExpenses projectExpenses={projectExpenses} />
          </Section>
          <Section
            title={t(
              "projectBudgetSynthesis.costsBreakdownByResourceType.title",
            )}
          >
            <div className={styles.costsBreakdownByResourceType}>
              <CostsBreakdownByResourceType synthesis={synthesis} />
            </div>
          </Section>
        </div>
        <div className={styles.row}>
          <Section title={t("projectBudgetSynthesis.expenses.title")}>
            <ExpensesTable
              projectExpenses={projectExpenses}
              project={project}
            />
          </Section>
        </div>
      </div>
      <div className={styles.exportButton}>
        <ExportButton
          export={() => {
            exportData({
              fileName: project.name,
              author: account,
              data: {
                type: ExportsDataTypes.BUDGET_REPORT,
                data: {
                  project,
                  synthesis,
                  projectExpenses,
                  timeUnit,
                },
              },
              format: ExportsFormats.EXCEL,
            });
          }}
        />
      </div>
    </div>
  );
}

export { BudgetPage };
